import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  CertificateItem,
  EmployeeCertificate,
  IAllCertificates,
} from 'src/app/shared/models/employee-certificates.model';
import { IPendingCertificate } from 'src/app/shared/models/employee.model';
import { EmployeeCertificatesService } from 'src/app/shared/services/employee-certificates.service';
import { dateToStringWithoutTimezone } from 'src/app/shared/services/helpers/date.helper';
import { errorMessage } from 'src/app/shared/utils';
import { DIALOG_BUTTONS } from '../../../shared/constants';
import { DialogComponent } from '../../../shared/dialogs';
import { AlertService } from '../../../shared/services';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  selector: 'app-certificates-dialog',
  templateUrl: './certificates-dialog.component.html',
  styleUrls: ['./certificates-dialog.component.scss'],
  standalone: false,
})
export class CertificatesDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private dialog = inject(MatDialog);
  private employeeCertificatesService = inject(EmployeeCertificatesService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  translate = inject(TranslateService);
  private alertService = inject(AlertService);
  dialogRef = inject<MatDialogRef<CertificatesDialogComponent>>(MatDialogRef);
  data = inject<{
    employeeId: number;
    showSubmitForm: boolean;
    collectOnly?: boolean;
    pendingItems?: IPendingCertificate[];
  }>(MAT_DIALOG_DATA);

  form: UntypedFormGroup;
  allCertificates$: Observable<IAllCertificates[]>;
  employeeCertificates: EmployeeCertificate[] = [];

  revalidate$ = new Subject<void>();

  displayedColumns = ['certificateName', 'validFrom', 'validTo', 'actions'];

  readonly sort = viewChild.required(MatSort);
  dataSource = new MatTableDataSource<EmployeeCertificate>([]);

  collectOnly = false;
  private localItems: IPendingCertificate[] = [];

  minValidTo = signal<Date | null>(null);

  private destroy$ = new Subject<void>();

  private initForm() {
    this.form = this.formBuilder.group({
      certificates: [null, [Validators.required]],
      validFrom: [null, [Validators.required]],
      validTo: [null],
    });
  }

  ngOnInit() {
    this.initForm();
    this.collectOnly = this.data.collectOnly ?? false;
    this.allCertificates$ = this.employeeCertificatesService.getAllCertificates();

    const validFromCtrl = this.form.controls.validFrom;
    validFromCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((validFrom: Date | null) => {
        this.minValidTo.set(validFrom);
        const validToCtrl = this.form.controls.validTo;
        if (validToCtrl.value && validFrom && validToCtrl.value < validFrom) {
          validToCtrl.setValue(null);
        }
      });

    if (this.collectOnly) {
      this.localItems = [...(this.data.pendingItems ?? [])];
      this.refreshLocalState();
    } else {
      this.revalidate$
        .pipe(
          switchMap(() =>
            this.employeeCertificatesService.getEmployeeCertificates(this.data.employeeId),
          ),
          takeUntil(this.destroy$),
        )
        .subscribe((data) => {
          this.employeeCertificates = data.map((cert: EmployeeCertificate) => ({
            ...cert,
            id: cert.certificateId,
          }));
          this.dataSource.data = this.employeeCertificates;
        });

      this.revalidate$.next();
    }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort();
  }

  removeCertificate(certificateId: number): void {
    this.employeeCertificatesService
      .deleteEmployeeCertificate(this.data.employeeId, certificateId)
      .subscribe({
        next: () => {
          this.revalidate$.next();
        },
        error: (err) => {
          this.alertService.showError(errorMessage(err));
        },
      });
  }

  submitHandler() {
    this.form.markAllAsTouched();

    if (!this.form.valid) return;

    if (this.collectOnly) {
      const newSelections: IPendingCertificate[] =
        this.form.value.certificates?.map((cert: CertificateItem) => ({
          certificateId: cert.id,
          certificateName: cert.name,
          validFrom: dateToStringWithoutTimezone(this.form.value.validFrom),
          validTo: dateToStringWithoutTimezone(this.form.value.validTo),
        })) ?? [];
      this.localItems = [...this.localItems, ...newSelections];
      this.refreshLocalState();
      this.form.reset();
      this.form.controls.certificates.setErrors(null);
      return;
    }

    combineLatest(
      this.form.value.certificates?.map((cert: CertificateItem) => {
        return this.employeeCertificatesService.createEmployeeCertificate({
          employeeId: this.data.employeeId,
          certificateId: cert.id,
          validTo: dateToStringWithoutTimezone(this.form.value.validTo),
          validFrom: dateToStringWithoutTimezone(this.form.value.validFrom),
        });
      }),
    ).subscribe({
      complete: () => {
        this.alertService.showSuccess('Certificate Added Successfully');

        this.form.reset();
        this.form.controls.certificates.setErrors(null);

        this.revalidate$.next();
      },
      error: (err) => {
        this.alertService.showError(errorMessage(err));
      },
    });
  }

  openDialog(certificate: EmployeeCertificate) {
    this.dialog
      .open(DialogComponent, {
        data: {
          title: this.translate.instant('USER.REIMBURSEMENT_REQUESTS.DELETE_CONFIRMATION'),
          description: `Do you want to delete "${certificate.certificateName}"?`,
          sharedButtonClass: DIALOG_BUTTONS.deleteButton,
          sharedButtonText: this.translate.instant('USER.REIMBURSEMENT_REQUESTS.DELETE'),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;

        if (this.collectOnly) {
          this.localItems = this.localItems.filter(
            (c) => c.certificateId !== certificate.certificateId,
          );
          this.refreshLocalState();
          return;
        }

        this.employeeCertificatesService
          .deleteEmployeeCertificate(this.data.employeeId, certificate.certificateId)
          .subscribe({
            next: () => {
              this.alertService.showSuccess('Certificate Deleted Successfully');
              this.revalidate$.next();
            },
            error: (err) => {
              this.alertService.showError(errorMessage(err));
            },
          });
      });
  }

  closeDialog() {
    if (this.collectOnly) {
      this.dialogRef.close(this.localItems);
      return;
    }
    this.dialogRef.close();
  }

  private refreshLocalState(): void {
    this.employeeCertificates = this.localItems.map((cert) => ({
      ...cert,
      id: cert.certificateId,
    })) as any;
    this.dataSource.data = this.employeeCertificates;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayFn(cert?: CertificateItem) {
    return cert?.name ?? '';
  }
}
